import { Braces, Calculator, FileCheck, FileMinus, FileText, RotateCcw, type LucideIcon } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { previewNumber, type DocNumberType } from "../../../../lib/sales";
import { Card, CardHead, listCard } from "../../../ui";

/** The four kinds that carry their own counter, in the order the sales rail
 *  lists them. */
export const DOC_TYPES: DocNumberType[] = ["invoice", "estimate", "proposal", "creditnote"];

/** Each kind's name and the icon the sidebar already gives it - the same mark
 *  in both places, so nobody has to read the row to know which one it is. */
const DOC_META: Record<DocNumberType, { labelKey: string; icon: LucideIcon }> = {
  invoice: { labelKey: "sales.invoices.title", icon: FileText },
  estimate: { labelKey: "sales.estimates.title", icon: Calculator },
  proposal: { labelKey: "sales.proposals.title", icon: FileCheck },
  creditnote: { labelKey: "sales.creditNotes.title", icon: FileMinus },
};

/** The placeholders the server's `formatNumber` understands. Written out once,
 *  in the footer, rather than repeated as four placeholders nobody reads. */
const TOKENS = "{YYYY} {YY} {MM} {DD} {NNNNN} {NNNN} {NNN} {N}";

/** Kind, template, what that template produces, and the counter itself. */
const COLS = "minmax(0,1.3fr) minmax(0,1.7fr) minmax(0,1.2fr) 132px";

/**
 * Settings -> Invoices -> document numbers: one pill row per document kind,
 * holding its number template and the next number that template will use.
 *
 * A row per kind rather than a matrix of cells: these are four objects with
 * settings on them, which is what the CRM's pill rows are for, and the preview
 * column means the effect of a template edit is readable on the same line
 * instead of under it.
 */
export function NumberingCard({
  templates,
  nextNums,
  onTemplate,
  onNext,
}: {
  templates: Record<DocNumberType, string>;
  nextNums: Record<DocNumberType, number>;
  onTemplate: (doc: DocNumberType, template: string) => void;
  onNext: (doc: DocNumberType, next: number) => void;
}) {
  const t = useT();

  return (
    <Card style={listCard(COLS)}>
      <CardHead title={t("settings.invoicing.numbering")} sub={t("settings.invoicing.numberingDesc")} />

      {/* 18 rather than the 22 a list uses when it opens a card: `crm-card-head`
          already lays down 4px under itself. */}
      <div className="crm-list-head" style={{ gridTemplateColumns: COLS, paddingTop: 18 }}>
        <span>{t("settings.invoicing.docType")}</span>
        <span>{t("settings.invoicing.template")}</span>
        <span>{t("settings.invoicing.preview")}</span>
        <span>{t("settings.invoicing.nextNumber")}</span>
      </div>

      <div className="crm-rows pt-0">
        {DOC_TYPES.map((doc) => {
          const { labelKey, icon: Icon } = DOC_META[doc];
          const template = templates[doc] ?? "";
          const next = nextNums[doc] ?? 1;

          return (
            // Soft corners rather than the full pill: the row carries two input
            // fields, and a 999px radius eats the first and last of them.
            <div key={doc} className="crm-list-row crm-list-row-soft" style={{ gridTemplateColumns: COLS }}>
              <span className="flex min-w-0 items-center gap-3">
                {/* A tinted disc, not an Avatar - a document kind has no name to
                    take initials from. */}
                <span className="crm-av crm-av-soft">
                  <Icon className="size-4" />
                </span>
                <span className="crm-prow-n crm-trunc min-w-0">{t(labelKey)}</span>
              </span>

              {/* Both fields name themselves after their row as well as their
                  column: four identically labelled boxes read as one field
                  repeated when the column head is out of earshot. */}
              <label className="crm-inp h-9 min-w-0 px-3.5">
                <input
                  value={template}
                  onChange={(e) => onTemplate(doc, e.target.value)}
                  placeholder="INV-{YYYY}-{NNNN}"
                  aria-label={`${t(labelKey)}: ${t("settings.invoicing.template")}`}
                  autoComplete="off"
                  spellCheck={false}
                  className="font-mono text-[12.75px]"
                />
              </label>

              <span className="crm-tnum crm-trunc min-w-0 font-mono text-[12.75px]">
                {previewNumber(template, next)}
              </span>

              <label className="crm-inp h-9 min-w-0 px-3.5">
                <input
                  type="number"
                  min={1}
                  value={next}
                  onChange={(e) => onNext(doc, parseInt(e.target.value, 10) || 1)}
                  aria-label={`${t(labelKey)}: ${t("settings.invoicing.nextNumber")}`}
                  title={t("settings.invoicing.nextHint")}
                  className="crm-tnum text-[12.75px]"
                />
              </label>
            </div>
          );
        })}
      </div>

      {/* The two things worth knowing about this card, in the footer slot the
          lists use - it inherits the same minimum width as the rows, so it does
          not sit still while they scroll. */}
      <div className="crm-tfoot flex-col items-start gap-1.5">
        <span className="flex min-w-0 items-center gap-2">
          <Braces className="size-3.5 flex-none" />
          <span className="min-w-0">
            {t("settings.invoicing.templateHint")}: <b className="font-mono font-normal text-foreground">{TOKENS}</b>
          </span>
        </span>
        <span className="flex min-w-0 items-center gap-2">
          <RotateCcw className="size-3.5 flex-none" />
          <span className="min-w-0">{t("settings.invoicing.nextHint")}</span>
        </span>
      </div>
    </Card>
  );
}
